import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import DateField from "./DateField";
import { ArrowSquareOut, MagnifyingGlass, FloppyDisk, MapPin, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Per-state configuration. Add a new state by extending this map. */
export const STATE_CONFIG = {
    NC: {
        name: "North Carolina",
        label: "North Carolina eCourts",
        badge: "Live · 100 counties",
        countiesEndpoint: "/court/nc/counties",
        infoEndpoint: null,
        lookupEndpoint: "/court/nc/lookup",
        captureEndpoint: "/court/nc/capture",
        sourceKey: "nc_ecourts",
        countyRequired: true,  // capture requires county
        countyRequiredForLookup: false,
        portalName: "NC AOC Tyler Odyssey",
        judgmentTypes: [
            { value: "civil_judgment", label: "Civil money judgment" },
            { value: "default_judgment", label: "Default judgment" },
            { value: "money_judgment", label: "Money judgment" },
            { value: "tax_lien", label: "Tax lien" },
            { value: "mechanics_lien", label: "Mechanics lien" },
            { value: "ucc_lien", label: "UCC lien" },
            { value: "domestic_judgment", label: "Domestic / family judgment" },
            { value: "other", label: "Other" },
        ],
        courtTypes: [
            { value: "district", label: "District court" },
            { value: "superior", label: "Superior court" },
            { value: "small_claims", label: "Small claims" },
            { value: "magistrate", label: "Magistrate" },
        ],
    },
    TX: {
        name: "Texas",
        label: "Texas Re:SearchTX",
        badge: "Statewide",
        countiesEndpoint: null,
        infoEndpoint: "/court/tx/info",
        lookupEndpoint: "/court/tx/lookup",
        captureEndpoint: "/court/tx/capture",
        sourceKey: "tx_research",
        countyRequired: false,
        countyRequiredForLookup: false,
        portalName: "Re:SearchTX (Texas OCA)",
        judgmentTypes: [
            { value: "civil_judgment", label: "Civil money judgment" },
            { value: "default_judgment", label: "Default judgment" },
            { value: "money_judgment", label: "Money judgment" },
            { value: "abstract_of_judgment", label: "Abstract of judgment" },
            { value: "tax_lien", label: "Tax lien" },
            { value: "mechanics_lien", label: "Mechanics lien" },
            { value: "ucc_lien", label: "UCC lien" },
            { value: "domestic_judgment", label: "Domestic / family judgment" },
            { value: "other", label: "Other" },
        ],
        courtTypes: [
            { value: "district", label: "District court" },
            { value: "county", label: "County court at law" },
            { value: "justice", label: "Justice of the peace" },
            { value: "constitutional_county", label: "Constitutional county" },
            { value: "probate", label: "Probate" },
        ],
    },
    FL: {
        name: "Florida",
        label: "Florida Clerk of Court",
        badge: "67 counties · 20 direct portals",
        countiesEndpoint: "/court/fl/counties",
        infoEndpoint: null,
        lookupEndpoint: "/court/fl/lookup",
        captureEndpoint: "/court/fl/capture",
        sourceKey: "fl_clerk",
        countyRequired: true,
        countyRequiredForLookup: true,
        portalName: "FL County Clerk of Court",
        judgmentTypes: [
            { value: "civil_judgment", label: "Civil money judgment" },
            { value: "default_judgment", label: "Default judgment" },
            { value: "money_judgment", label: "Money judgment" },
            { value: "tax_lien", label: "Tax lien" },
            { value: "mechanics_lien", label: "Mechanics lien" },
            { value: "ucc_lien", label: "UCC lien" },
            { value: "code_enforcement_lien", label: "Code enforcement lien" },
            { value: "domestic_judgment", label: "Domestic / family judgment" },
            { value: "other", label: "Other" },
        ],
        courtTypes: [
            { value: "circuit", label: "Circuit court" },
            { value: "county", label: "County court" },
            { value: "small_claims", label: "Small claims" },
        ],
    },
    VA: {
        name: "Virginia",
        label: "Virginia OCIS",
        badge: "Statewide · District + Circuit",
        countiesEndpoint: null,
        infoEndpoint: "/court/va/info",
        lookupEndpoint: "/court/va/lookup",
        captureEndpoint: "/court/va/capture",
        sourceKey: "va_ocis",
        countyRequired: false,
        countyRequiredForLookup: false,
        portalName: "Virginia OCIS (Office of the Executive Secretary)",
        judgmentTypes: [
            { value: "civil_judgment", label: "Civil money judgment" },
            { value: "default_judgment", label: "Default judgment" },
            { value: "money_judgment", label: "Money judgment" },
            { value: "abstract_of_judgment", label: "Abstract of judgment" },
            { value: "tax_lien", label: "Tax lien" },
            { value: "mechanics_lien", label: "Mechanics lien" },
            { value: "ucc_lien", label: "UCC lien" },
            { value: "domestic_judgment", label: "Domestic / family judgment" },
            { value: "other", label: "Other" },
        ],
        courtTypes: [
            { value: "general_district", label: "General District" },
            { value: "circuit", label: "Circuit court" },
            { value: "juvenile_domestic", label: "Juvenile & Domestic Relations" },
            { value: "combined", label: "Combined court" },
        ],
    },
    SC: {
        name: "South Carolina",
        label: "South Carolina Public Index",
        badge: "Statewide · 46 counties",
        countiesEndpoint: "/court/sc/counties",
        infoEndpoint: null,
        lookupEndpoint: "/court/sc/lookup",
        captureEndpoint: "/court/sc/capture",
        sourceKey: "sc_public_index",
        countyRequired: true,
        countyRequiredForLookup: false,
        portalName: "SC Judicial Branch Public Index",
        judgmentTypes: [
            { value: "civil_judgment", label: "Civil money judgment" },
            { value: "default_judgment", label: "Default judgment" },
            { value: "money_judgment", label: "Money judgment" },
            { value: "magistrate_judgment", label: "Magistrate judgment" },
            { value: "tax_lien", label: "Tax lien" },
            { value: "mechanics_lien", label: "Mechanics lien" },
            { value: "ucc_lien", label: "UCC lien" },
            { value: "domestic_judgment", label: "Domestic / family judgment" },
            { value: "other", label: "Other" },
        ],
        courtTypes: [
            { value: "common_pleas", label: "Court of Common Pleas" },
            { value: "magistrate", label: "Magistrate court" },
            { value: "family", label: "Family court" },
            { value: "circuit", label: "Circuit court" },
        ],
    },
    NY: {
        name: "New York",
        label: "New York eCourts (WebCivil)",
        badge: "Statewide · 62 counties",
        countiesEndpoint: "/court/ny/counties",
        infoEndpoint: null,
        lookupEndpoint: "/court/ny/lookup",
        captureEndpoint: "/court/ny/capture",
        sourceKey: "ny_ecourts",
        countyRequired: false,
        countyRequiredForLookup: false,
        portalName: "NY Unified Court System eCourts",
        judgmentTypes: [
            { value: "civil_judgment", label: "Civil money judgment" },
            { value: "default_judgment", label: "Default judgment" },
            { value: "money_judgment", label: "Money judgment" },
            { value: "transcript_of_judgment", label: "Transcript of judgment" },
            { value: "tax_lien", label: "Tax lien" },
            { value: "mechanics_lien", label: "Mechanics lien" },
            { value: "ucc_lien", label: "UCC lien" },
            { value: "domestic_judgment", label: "Domestic / family judgment" },
            { value: "other", label: "Other" },
        ],
        courtTypes: [
            { value: "supreme", label: "Supreme Court" },
            { value: "civil_court", label: "Civil Court (NYC)" },
            { value: "county", label: "County court" },
            { value: "city", label: "City court" },
            { value: "town_village", label: "Town / village justice" },
            { value: "family", label: "Family court" },
            { value: "surrogate", label: "Surrogate's court" },
        ],
    },
};

// Universal judgment + court types used by the factory-driven extra states.
const UNIVERSAL_JUDGMENT_TYPES = [
    { value: "civil_judgment", label: "Civil money judgment" },
    { value: "default_judgment", label: "Default judgment" },
    { value: "money_judgment", label: "Money judgment" },
    { value: "abstract_of_judgment", label: "Abstract / transcript of judgment" },
    { value: "tax_lien", label: "Tax lien" },
    { value: "mechanics_lien", label: "Mechanics lien" },
    { value: "ucc_lien", label: "UCC lien" },
    { value: "domestic_judgment", label: "Domestic / family judgment" },
    { value: "other", label: "Other" },
];
const UNIVERSAL_COURT_TYPES = [
    { value: "trial", label: "Trial / civil court" },
    { value: "small_claims", label: "Small claims" },
    { value: "magistrate", label: "Magistrate / justice" },
    { value: "appellate", label: "Appellate" },
    { value: "family", label: "Family / domestic" },
    { value: "other", label: "Other" },
];

const EXTRA_STATES = [
    { code: "CA", name: "California",    label: "California — county portals",          badge: "58 counties · 10 direct portals", portalName: "CA Superior Court (county)",  needCounty: true,  needCountyLookup: true  },
    { code: "OH", name: "Ohio",          label: "Ohio Clerks of Court",                 badge: "88 counties · 8 direct portals",  portalName: "OH County Clerk of Courts",   needCounty: true,  needCountyLookup: true  },
    { code: "WV", name: "West Virginia", label: "West Virginia Judiciary",              badge: "55 counties · directory",         portalName: "WV Judiciary public resources", needCounty: false, needCountyLookup: false },
    { code: "PA", name: "Pennsylvania",  label: "Pennsylvania UJS Portal",              badge: "Statewide · 67 counties",         portalName: "PA Unified Judicial System",  needCounty: false, needCountyLookup: false },
    { code: "MD", name: "Maryland",      label: "Maryland Judiciary Case Search",       badge: "Statewide",                       portalName: "MD Judiciary Case Search",    needCounty: false, needCountyLookup: false },
    { code: "CT", name: "Connecticut",   label: "Connecticut Judicial Civil/Family",    badge: "Statewide",                       portalName: "CT Judicial Branch Civil",    needCounty: false, needCountyLookup: false },
    { code: "ME", name: "Maine",         label: "Maine Judicial Branch records",        badge: "16 counties · directory",         portalName: "Maine Judicial records",      needCounty: false, needCountyLookup: false },
    { code: "WA", name: "Washington",    label: "Washington Courts Data Warehouse",     badge: "Statewide · 39 counties",         portalName: "WA Courts Data Warehouse",    needCounty: false, needCountyLookup: false },
    { code: "GA", name: "Georgia",       label: "Georgia GSCCCA + county clerks",       badge: "Statewide · 159 counties",        portalName: "GSCCCA lien & UCC index",     needCounty: true,  needCountyLookup: false },
    { code: "IL", name: "Illinois",      label: "Illinois Judici + county portals",     badge: "80+ counties · 8 direct portals", portalName: "Judici + county portals",     needCounty: true,  needCountyLookup: false },
    { code: "MI", name: "Michigan",      label: "Michigan Courts directory",            badge: "83 counties · 7 direct portals",  portalName: "MI Courts directory",         needCounty: true,  needCountyLookup: false },
    { code: "NJ", name: "New Jersey",    label: "NJ Civil & Foreclosure ACMS",          badge: "Statewide · 21 counties",         portalName: "NJ Civil & Foreclosure ACMS", needCounty: false, needCountyLookup: false },
];

for (const s of EXTRA_STATES) {
    const lc = s.code.toLowerCase();
    STATE_CONFIG[s.code] = {
        name: s.name,
        label: s.label,
        badge: s.badge,
        countiesEndpoint: `/court/${lc}/counties`,
        infoEndpoint: null,
        lookupEndpoint: `/court/${lc}/lookup`,
        captureEndpoint: `/court/${lc}/capture`,
        sourceKey: `${lc}_portal`,
        countyRequired: s.needCounty,
        countyRequiredForLookup: s.needCountyLookup,
        portalName: s.portalName,
        judgmentTypes: UNIVERSAL_JUDGMENT_TYPES,
        courtTypes: UNIVERSAL_COURT_TYPES,
    };
}

/**
 * State-court deep-link + capture helper.
 *
 *  - selectMode (no caseId, with onSelect): pre-fills the parent NewCase form.
 *  - With caseId: lands the captured judgment directly onto the case.
 */
export default function StateCourtPanel({ stateCode, caseId, onSelect }) {
    const cfg = STATE_CONFIG[stateCode];
    const selectMode = !caseId && typeof onSelect === "function";

    const [counties, setCounties] = useState([]);
    const [search, setSearch] = useState({
        last_name: "", first_name: "", business_name: "", case_number: "", county: "",
    });
    const [searching, setSearching] = useState(false);
    const [lookupResult, setLookupResult] = useState(null);
    const [capture, setCapture] = useState({
        case_number: "", county: "", judgment_date: "", judgment_amount: "",
        judgment_type: cfg?.judgmentTypes?.[0]?.value || "civil_judgment",
        plaintiff: "", defendant: "",
        court_type: "", filed_date: "", portal_url: "", notes: "",
    });
    const [saving, setSaving] = useState(false);

    // Initial state is derived from cfg via useState above; on mount, load counties.
    // The parent uses key={stateCode} so this component remounts on state change,
    // which eliminates any prop-derived reset logic.
    useEffect(() => {
        if (!cfg) return;
        if (cfg.countiesEndpoint) {
            api.get(cfg.countiesEndpoint).then((r) => setCounties(r.data.counties || [])).catch(() => setCounties([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!cfg) return null;

    const openPortal = async () => {
        if (cfg.countyRequiredForLookup && !search.county) {
            toast.error("Select a county to search");
            return;
        }
        // ---- Critical: open the new tab SYNCHRONOUSLY inside the user gesture ----
        // We use about:blank as a placeholder and redirect once the API resolves.
        // We deliberately drop `noopener` here so we can set .location later; the
        // about:blank tab is same-origin until redirect, so this is safe.
        const placeholder = window.open("about:blank", "_blank");
        const popupBlocked = !placeholder;

        setSearching(true);
        setLookupResult(null);
        try {
            const { data } = await api.post(cfg.lookupEndpoint, {
                last_name: search.last_name || null,
                first_name: search.first_name || null,
                business_name: search.business_name || null,
                case_number: search.case_number || null,
                ...(counties.length || cfg.countyRequiredForLookup ? { county: search.county || null } : {}),
            });

            // Render the result card so the user always has a clickable Open link
            setLookupResult({
                portal_url: data.portal_url,
                search_query: data.search_query,
                portal_name: cfg.portalName,
            });

            // Best-effort clipboard copy
            try {
                await navigator.clipboard.writeText(data.search_query);
                toast.success(`Search query "${data.search_query}" copied to clipboard`);
            } catch {
                toast.info(`Paste this in the portal: ${data.search_query}`);
            }

            // Redirect the pre-opened tab (or warn if popup-blocked)
            if (placeholder && !placeholder.closed) {
                try {
                    placeholder.location.href = data.portal_url;
                } catch {
                    // Cross-origin restrictions can throw — fall through to manual click
                }
            } else if (popupBlocked) {
                toast.warning("Your browser blocked the portal popup — click 'Open portal' below.");
            }

            if (search.county) setCapture((c) => ({ ...c, county: c.county || search.county }));
        } catch (e) {
            if (placeholder && !placeholder.closed) placeholder.close();
            toast.error(formatApiError(e));
        } finally {
            setSearching(false);
        }
    };

    const submitCapture = async () => {
        if (!capture.case_number.trim()) { toast.error("Case number required"); return; }
        if (cfg.countyRequired && !capture.county) { toast.error("County required"); return; }

        const payload = {
            case_number: capture.case_number.trim(),
            ...(counties.length ? { county: capture.county || null } : {}),
            judgment_date: capture.judgment_date || null,
            judgment_amount: capture.judgment_amount ? parseFloat(capture.judgment_amount) : null,
            judgment_type: capture.judgment_type,
            plaintiff: capture.plaintiff || null,
            defendant: capture.defendant || null,
            court_type: capture.court_type || null,
            filed_date: capture.filed_date || null,
            portal_url: capture.portal_url || null,
            notes: capture.notes || null,
        };

        if (selectMode) {
            onSelect({ source: cfg.sourceKey, state: stateCode, ...payload });
            toast.success(`${cfg.name} judgment captured into the new-case form`);
            return;
        }

        setSaving(true);
        try {
            await api.post(cfg.captureEndpoint, { case_id: caseId, ...payload });
            toast.success(`${cfg.name} judgment saved to this case`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="p-5 space-y-4" data-testid={`state-panel-${stateCode}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <MapPin size={18} weight="duotone" className="text-[#0F1E3D]" />
                        <h3 className="font-serif text-lg text-[#0F1E3D]">{cfg.label}</h3>
                        <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">{cfg.badge}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                        We open the official {cfg.portalName} portal pre-loaded for your query. Find the judgment, then capture it back into {selectMode ? "this new case form" : "this case"}.
                    </p>
                </div>
            </div>

            <Tabs defaultValue="search" className="space-y-4">
                <TabsList data-testid={`state-tabs-${stateCode}`}>
                    <TabsTrigger value="search" data-testid={`state-tab-search-${stateCode}`}>
                        <MagnifyingGlass size={13} className="mr-1.5" /> Search portal
                    </TabsTrigger>
                    <TabsTrigger value="capture" data-testid={`state-tab-capture-${stateCode}`}>
                        <FloppyDisk size={13} className="mr-1.5" /> Capture details
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="search" className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">First name</Label>
                            <Input value={search.first_name} onChange={(e) => setSearch({ ...search, first_name: e.target.value })} className="h-10" data-testid={`state-first-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Last name</Label>
                            <Input value={search.last_name} onChange={(e) => setSearch({ ...search, last_name: e.target.value })} className="h-10" data-testid={`state-last-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Business name (optional)</Label>
                            <Input value={search.business_name} onChange={(e) => setSearch({ ...search, business_name: e.target.value })} className="h-10" placeholder="e.g. Acme LLC" data-testid={`state-business-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Case number (optional)</Label>
                            <Input value={search.case_number} onChange={(e) => setSearch({ ...search, case_number: e.target.value })} className="h-10" data-testid={`state-caseno-${stateCode}`} />
                        </div>
                        {counties.length > 0 && (
                            <div className="sm:col-span-2">
                                <Label className="text-xs">
                                    County {cfg.countyRequiredForLookup ? "*" : "(optional)"}
                                </Label>
                                <Select value={search.county} onValueChange={(v) => setSearch({ ...search, county: v })}>
                                    <SelectTrigger className="h-10" data-testid={`state-county-${stateCode}`}>
                                        <SelectValue placeholder={`All ${cfg.name} counties`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {counties.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <Button onClick={openPortal} disabled={searching} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10" data-testid={`state-open-portal-${stateCode}`}>
                        <ArrowSquareOut size={14} className="mr-1.5" />
                        {searching ? "Opening…" : `Open ${cfg.name} portal`}
                    </Button>
                    <p className="text-[11px] text-slate-500 italic">
                        On desktop we copy your search to the clipboard automatically. On iPad / iOS, tap <strong>Copy</strong> in the result card after searching, then paste into the portal&apos;s search box.
                    </p>

                    {lookupResult && (
                        <div
                            className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3.5"
                            data-testid={`state-lookup-result-${stateCode}`}
                        >
                            <div className="flex items-start gap-3">
                                <ArrowSquareOut size={18} weight="duotone" className="text-emerald-700 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-emerald-900">Portal ready</p>
                                    <p className="text-[11px] text-emerald-800/80 mt-1 leading-snug">
                                        Some state portals show a one-time human-verification — tap <strong>Begin</strong>, then paste your search query into the portal&apos;s search box.
                                    </p>
                                </div>
                                <a
                                    href={lookupResult.portal_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`state-lookup-fallback-link-${stateCode}`}
                                >
                                    <Button size="sm" className="h-9 bg-emerald-700 hover:bg-emerald-800 shrink-0">
                                        Open portal
                                    </Button>
                                </a>
                            </div>

                            {/* iPad/iOS safe — explicit user-gesture copy. Renders the query
                                visibly so users can also long-press → Copy as a fallback. */}
                            <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center gap-2">
                                <code
                                    className="flex-1 min-w-0 truncate font-mono text-sm bg-white border border-emerald-200 rounded px-2.5 py-1.5 select-all"
                                    data-testid={`state-search-query-${stateCode}`}
                                >
                                    {lookupResult.search_query}
                                </code>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-9 shrink-0"
                                    onClick={() => {
                                        // Inline IIFE so it runs synchronously inside this user gesture.
                                        const text = lookupResult.search_query;
                                        const finish = (ok) => {
                                            if (ok) toast.success("Copied to clipboard");
                                            else toast.error("Copy blocked — long-press the text and choose Copy");
                                        };
                                        // Primary: modern clipboard API (works on iOS in direct gesture)
                                        if (navigator.clipboard && navigator.clipboard.writeText) {
                                            navigator.clipboard.writeText(text).then(() => finish(true)).catch(() => {
                                                // Fallback: hidden textarea + execCommand (legacy, broadly supported)
                                                try {
                                                    const ta = document.createElement("textarea");
                                                    ta.value = text;
                                                    ta.style.position = "fixed";
                                                    ta.style.opacity = "0";
                                                    document.body.appendChild(ta);
                                                    ta.focus();
                                                    ta.select();
                                                    const ok = document.execCommand("copy");
                                                    document.body.removeChild(ta);
                                                    finish(ok);
                                                } catch {
                                                    finish(false);
                                                }
                                            });
                                        } else {
                                            // No async API at all — go straight to legacy
                                            try {
                                                const ta = document.createElement("textarea");
                                                ta.value = text;
                                                ta.style.position = "fixed";
                                                ta.style.opacity = "0";
                                                document.body.appendChild(ta);
                                                ta.focus(); ta.select();
                                                const ok = document.execCommand("copy");
                                                document.body.removeChild(ta);
                                                finish(ok);
                                            } catch { finish(false); }
                                        }
                                    }}
                                    data-testid={`state-copy-query-${stateCode}`}
                                >
                                    <Copy size={13} className="mr-1.5" /> Copy
                                </Button>
                            </div>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="capture" className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Case number *</Label>
                            <Input value={capture.case_number} onChange={(e) => setCapture({ ...capture, case_number: e.target.value })} className="h-10 font-mono" data-testid={`state-cap-caseno-${stateCode}`} />
                        </div>
                        {counties.length > 0 && (
                            <div>
                                <Label className="text-xs">County {cfg.countyRequired ? "*" : ""}</Label>
                                <Select value={capture.county} onValueChange={(v) => setCapture({ ...capture, county: v })}>
                                    <SelectTrigger className="h-10" data-testid={`state-cap-county-${stateCode}`}>
                                        <SelectValue placeholder="Select county" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {counties.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div>
                            <Label className="text-xs">Judgment type</Label>
                            <Select value={capture.judgment_type} onValueChange={(v) => setCapture({ ...capture, judgment_type: v })}>
                                <SelectTrigger className="h-10" data-testid={`state-cap-jtype-${stateCode}`}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {cfg.judgmentTypes.map((j) => (
                                        <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Court type</Label>
                            <Select value={capture.court_type} onValueChange={(v) => setCapture({ ...capture, court_type: v })}>
                                <SelectTrigger className="h-10" data-testid={`state-cap-ctype-${stateCode}`}>
                                    <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cfg.courtTypes.map((c) => (
                                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Plaintiff / Creditor</Label>
                            <Input value={capture.plaintiff} onChange={(e) => setCapture({ ...capture, plaintiff: e.target.value })} className="h-10" data-testid={`state-cap-plaintiff-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Defendant</Label>
                            <Input value={capture.defendant} onChange={(e) => setCapture({ ...capture, defendant: e.target.value })} className="h-10" data-testid={`state-cap-defendant-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Filed date</Label>
                            <DateField value={capture.filed_date} onChange={(e) => setCapture({ ...capture, filed_date: e.target.value })} data-testid={`state-cap-filed-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Judgment date</Label>
                            <DateField value={capture.judgment_date} onChange={(e) => setCapture({ ...capture, judgment_date: e.target.value })} data-testid={`state-cap-jdate-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Judgment amount ($)</Label>
                            <Input type="number" value={capture.judgment_amount} onChange={(e) => setCapture({ ...capture, judgment_amount: e.target.value })} className="h-10 font-mono" min="0" data-testid={`state-cap-amount-${stateCode}`} />
                        </div>
                        <div>
                            <Label className="text-xs">Portal URL (optional)</Label>
                            <Input value={capture.portal_url} onChange={(e) => setCapture({ ...capture, portal_url: e.target.value })} placeholder="Paste the case URL from the portal" className="h-10" data-testid={`state-cap-url-${stateCode}`} />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-xs">Notes</Label>
                            <Input value={capture.notes} onChange={(e) => setCapture({ ...capture, notes: e.target.value })} className="h-10" data-testid={`state-cap-notes-${stateCode}`} />
                        </div>
                    </div>
                    <Button onClick={submitCapture} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 h-10" data-testid={`state-cap-submit-${stateCode}`}>
                        <FloppyDisk size={14} className="mr-1.5" />
                        {saving ? "Saving…" : selectMode ? "Use these details in form" : `Save ${cfg.name} record to case`}
                    </Button>
                </TabsContent>
            </Tabs>
        </Card>
    );
}
